import { memo, Dispatch } from "react";
import { Box, SvgIcon, Typography } from "@mui/material";
// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
import SimpleIconButton from "@/Layouts/components/Buttons/SimpleIconButton";
import FranchisesLayouts from "./FranchisesLayouts";
// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";

// Utils
import {
	findError,
	getOemFranchiseDisabled,
	getOemFranchisesSelected,
} from "@/Settings/helpers/AdministrationTools/LocationDealerCodes/utils";
import {
	TAction,
	EActionType,
	ILocationDealerCode,
	TLocationDealerCodeState,
	IOemMetaData,
	TErrors,
} from "@/Settings/types/administrationTools/locationDealerCodes";

// Types
import { IStoreDetails } from "@/Common/interfaces/location";

type OemLayoutProps = {
	oem: ILocationDealerCode;
	oems: TLocationDealerCodeState;
	oemMetaData: IOemMetaData[];
	dispatch: Dispatch<TAction>;
	warningPaths: number[][];
	onSetWarningPaths: (paths: number[], action: string) => void;
	oemIndex: number;
	isPending: boolean;
	errors: TErrors;
	storesDetails: IStoreDetails[];
	disabledStores: string[];
};

const excludeFranchises = ["N/A", ""];

const OemLayout = memo((props: OemLayoutProps) => {
	const {
		oem,
		oems,
		oemMetaData,
		dispatch,
		oemIndex,
		errors,
		warningPaths,
		onSetWarningPaths,
		isPending,
		storesDetails,
		disabledStores,
	} = props;

	const handleOemChange = (selected: ILocationDealerCode) => {
		onSetWarningPaths([oemIndex], "remove");
		dispatch({
			type: EActionType.UPDATE_OEM,
			payload: {
				oemIndex,
				oemName: selected.oemName,
				oemReference: selected.oemReference,
				version: selected.version,
				financialStatementVersion: selected.financialStatementVersion,
				dealerCodeFS: "",
				legalName: "",
				dba: "",
			},
		});
	};

	const handleOemInputTextChange = (param: { dealerCodeFS?: string; legalName?: string; dba?: string }) => {
		dispatch({
			type: EActionType.UPDATE_OEM,
			payload: {
				oemIndex,
				...param,
			},
		});
	};

	const handleAddFranchise = () => {
		dispatch({
			type: EActionType.ADD_FRANCHISE,
			payload: {
				oemIndex,
			},
		});
	};

	const handleDeleteOem = () => {
		dispatch({
			type: EActionType.DELETE_OEM,
			payload: {
				oemIndex,
			},
		});
	};

	const foundOemMetaData = oemMetaData?.find((entry) => entry.oemReference === oem.oemReference);
	const disabledFoundFranchisesNames = getOemFranchiseDisabled(foundOemMetaData);
	const franchiseSelected = [...disabledFoundFranchisesNames, ...getOemFranchisesSelected(oem)];
	const isFranchiseCountInvalid =
		franchiseSelected.length === foundOemMetaData?.franchisesNames.length ||
		(foundOemMetaData?.franchisesNames.length ?? 0) - disabledFoundFranchisesNames.length <= 1;

	const isNASelected = oems.find((item) => item.oemReference === "N/A");
	const disabledAllFranchisesNames = oemMetaData.flatMap((item) => getOemFranchiseDisabled(item)) ?? [];
	const franchiseAllSelected = [...disabledAllFranchisesNames, ...oems.flatMap((oemSelected) => getOemFranchisesSelected(oemSelected))];

	const oemDisabled = oemMetaData
		.filter((item) => !item.franchisesNames.some((franchiseName) => ![...franchiseAllSelected].includes(franchiseName)))
		.map((item) => item.oemReference);

	if (isNASelected) {
		oemDisabled.push("N/A");
	}

	return (
		<Box className="mt-[16px]">
			<Box className="flex !mb-[32px]">
				<Box className="mr-[12px]">
					<Typography className="!font-montserrat !font-medium text-[16px] text-center text-default !mb-[12px]">OEM</Typography>
					<SmartSelector
						classes="!w-[220px]"
						showBy="oemName"
						selectBy="oemReference"
						options={[...oemMetaData, { oemReference: "N/A", oemName: "N/A" }]}
						selected={oem.oemReference}
						disabledOptions={oemDisabled}
						inputProps={{
							placeholder: "Select Branch",
						}}
						error={!!findError(errors, [oemIndex, "oemReference"])}
						onChangeAction={(selected) => handleOemChange(selected as ILocationDealerCode)}
						disabled={isPending}
					/>
				</Box>
				<Box className="mr-[12px]">
					<Typography className="!font-montserrat !font-medium text-[16px] text-center text-default !mb-[12px]">
						Dealer Code (FS)
					</Typography>
					<SimpleTextField
						classes="!w-[220px]"
						editable
						outlined
						text={oem.dealerCodeFS}
						placeholder={"i.e. BAC, Dealer Code"}
						inputProps={{ maxLength: 8 }}
						onActionUpdate={(text: string) => handleOemInputTextChange({ dealerCodeFS: text })}
						error={!!findError(errors, [oemIndex, "dealerCodeFS"])}
						disabled={isPending}
					/>
				</Box>
				<Box className="mr-[12px]">
					<Typography className="!font-montserrat !font-medium text-[16px] text-center text-default !mb-[12px]">
						Doing Business As (DBA)
					</Typography>
					<SmartSelector
						classes="!w-[220px]"
						showBy="name"
						selectBy="name"
						options={storesDetails}
						selected={oem.dba}
						disabledOptions={disabledStores}
						inputProps={{
							placeholder: "Select Store DBA",
						}}
						error={!!findError(errors, [oemIndex, "oemReference"])}
						onChangeAction={(selected) => handleOemInputTextChange({ dba: (selected as IStoreDetails).name })}
						disabled={isPending}
					/>
				</Box>
				<Box className="mr-[5px]">
					<Typography className="!font-montserrat !font-medium text-[16px] text-center text-default !mb-[12px]">
						Legal Name
					</Typography>
					<SimpleTextField
						classes="!w-[220px]"
						editable
						outlined
						text={oem.legalName}
						placeholder={"Legal Name"}
						onActionUpdate={(text: string) => handleOemInputTextChange({ legalName: text })}
						error={!!findError(errors, [oemIndex, "legalName"])}
						disabled={isPending}
					/>
				</Box>
				<Box className="flex items-end">
					<SimpleDoubleChecking
						className={"btn-delete !mt-[-3px]"}
						color="var(--gray-blue-color)"
						confirmationButtonColor="var(--gray-blue-color)"
						icon={<CloseRoundedIcon fontSize="small" />}
						iconHover={<CloseRoundedIcon fontSize="small" />}
						confirmationIcon={<SvgIcon className="!text-[25px] pl-[5px] pt-[2px]" component={TrashIcon} />}
						onDeleteAction={handleDeleteOem}
						disabled={isPending || oems.length === 1}
						size="small"
					/>
				</Box>
			</Box>
			{!excludeFranchises.includes(oem.oemReference) && (
				<>
					<Box>
						{oem.franchises?.map((item, franchiseIndex) => {
							const keyIndex = `${oem.oemReference}-${item.franchise}-${franchiseIndex}`;
							return (
								<FranchisesLayouts
									key={keyIndex}
									franchise={item}
									oemMetaData={foundOemMetaData}
									oemIndex={oemIndex}
									franchiseIndex={franchiseIndex}
									dispatch={dispatch}
									franchiseSelected={franchiseAllSelected}
									oem={oem}
									errors={errors}
									warningPaths={warningPaths}
									onSetWarningPaths={onSetWarningPaths}
									isPending={isPending}
								/>
							);
						})}
					</Box>
					<Box mt={1} display="flex" justifyContent="center" width="1000px">
						<SimpleIconButton
							className={"btn-add"}
							color={"var(--primary-color)"}
							icon={<AddCircleOutlineRoundedIcon fontSize="medium" sx={{ fontSize: "30px" }} />}
							iconHover={<AddCircleRounded fontSize="medium" sx={{ fontSize: "30px" }} />}
							disabled={isPending || !oem.oemReference || isFranchiseCountInvalid}
							onClick={handleAddFranchise}
						/>
					</Box>
				</>
			)}
		</Box>
	);
});

export default OemLayout;
